import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { jobsAPI, jobCategoriesAPI } from '../services/api';
import { Job, JobCategory } from '../types';
import JobCard from '../components/JobCard';
import Loading from '../components/Loading';
import { SEO } from '../components/seo/SEO';
import { buildSiteJsonLd } from '../utils/jsonLd';

const Home: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryTree, setCategoryTree] = useState<JobCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jobsResponse = await jobsAPI.getJobs({ per_page: 6 });
        setJobs(jobsResponse.jobs);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    jobCategoriesAPI.getTree({ withCounts: true })
      .then((res) => { if (!cancelled) setCategoryTree(res.categories || []); })
      .catch(() => { if (!cancelled) setCategoryTree([]); })
      .finally(() => { if (!cancelled) setCategoriesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/dashboard';
    if (user.role === 'recruiter') return '/recruiter/dashboard';
    if (user.role === 'candidate') return '/candidate/dashboard';
    return '/dashboard';
  };

  const getProfileLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/profile';
    if (user.role === 'recruiter') return '/recruiter/profile';
    if (user.role === 'partner') return '/partner/profile';
    return '/profile';
  };

  return (
    <div className="container">
      <SEO
        title="talixjobs — Hire smarter. Apply faster."
        description="AI-powered job matching. Browse thousands of open roles across India and apply with one click. Recruiters: post jobs, track candidates, and hire faster."
        jsonLd={buildSiteJsonLd()}
      />
      {/* Hero Section */}
      <section className="hero card shadow">
        <div>
          <h1 className="h1">Hire smarter. Apply faster.</h1>
          <p className="muted">
            talixjobs uses AI to match candidates with job descriptions so recruiters see the best profiles first.
          </p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
            <Link className="btn" to="/jobs">
              Explore Open Roles
            </Link>
            {isAuthenticated ? (
              <Link className="btn secondary" to={getDashboardLink()}>
                Go to Dashboard
              </Link>
            ) : (
              <Link className="btn secondary" to="/register">
                For Recruiters
              </Link>
            )}
          </div>
        </div>
        
        <div className="card">
          <div className="h2">Quick Links</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0 0' }}>
            {isAuthenticated ? (
              <>
                <li style={{ marginBottom: '8px' }}>
                  <Link to={getDashboardLink()}>Dashboard</Link>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Link to="/jobs">Browse Jobs</Link>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Link to={getProfileLink()}>My Profile</Link>
                </li>
              </>
            ) : (
              <>
                <li style={{ marginBottom: '8px' }}>
                  <Link to="/login">Candidate Login</Link>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Link to="/login">Recruiter Login</Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </section>

      {/* Browse by Category Section */}
      <section style={{ margin: '48px 0' }}>
        <h2 className="h1" style={{ marginBottom: '24px' }}>Browse by category</h2>
        {categoriesLoading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 16,
          }}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} style={{
                background: '#f3f4f6',
                height: 96,
                borderRadius: 8,
                opacity: 0.5,
              }} />
            ))}
          </div>
        ) : categoryTree.length === 0 ? null : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 16,
          }}>
            {categoryTree.map((parent) => (
              <Link
                key={parent.id}
                to={`/jobs/category/${parent.slug}`}
                style={{
                  display: 'block',
                  padding: 16,
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: 8,
                  background: 'var(--surface, #fff)',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 16 }}>{parent.name}</div>
                <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
                  {parent.active_job_count ?? 0} open role{parent.active_job_count === 1 ? '' : 's'}
                </div>
                {parent.description && (
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                    {parent.description}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* How It Works + Platform Commitments */}
      <section style={{ marginTop: '32px' }}>
        <h2 className="h1" style={{ textAlign: 'center', marginBottom: '24px' }}>
          How It Works
        </h2>
        <div className="grid grid-3" style={{ marginBottom: '24px' }}>
          <div className="card">
            <h3 className="h2">For Recruiters</h3>
            <p className="muted">Post role, review qualified matches, and move to shortlist faster.</p>
          </div>
          <div className="card">
            <h3 className="h2">For Candidates</h3>
            <p className="muted">Build profile, discover relevant roles, and apply with confidence.</p>
          </div>
          <div className="card">
            <h3 className="h2">For Partners</h3>
            <p className="muted">Share affiliate links, track referrals, and scale outcomes transparently.</p>
          </div>
        </div>
        <div className="card shadow">
          <h3 className="h2">Platform Commitments</h3>
          <ul style={{ margin: '8px 0 0 18px', color: '#4B5563' }}>
            <li>Privacy-first hiring workflows</li>
            <li>No contact details allowed in job descriptions</li>
            <li>Verified profiles and role-based access controls</li>
          </ul>
        </div>
      </section>

      {/* Recent Jobs Section */}
      <section style={{ marginTop: '48px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 className="h1">Latest Opportunities</h2>
          <Link to="/jobs" className="btn ghost">
            View All Jobs
          </Link>
        </div>

        {loading ? (
          <Loading text="Loading latest jobs..." />
        ) : (
          <div className="grid grid-2">
            {jobs.map(job => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}

        {!loading && jobs.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">💼</div>
            <h3 className="h2">No jobs available</h3>
            <p className="muted">Check back later for new opportunities</p>
          </div>
        )}
      </section>

      {/* Features Section */}
      <section style={{ marginTop: '48px' }}>
        <h2 className="h1" style={{ textAlign: 'center', marginBottom: '32px' }}>
          Why Choose talixjobs?
        </h2>
        
        <div className="grid grid-3">
          <div className="card">
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>🤖</div>
            <h3 className="h2">AI-Powered Matching</h3>
            <p className="muted">
              Our advanced AI analyzes your skills and experience to match you with the most relevant opportunities.
            </p>
          </div>
          
          <div className="card">
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚡</div>
            <h3 className="h2">Fast Applications</h3>
            <p className="muted">
              Apply to jobs with one click. Our system automatically calculates your match score for each position.
            </p>
          </div>
          
          <div className="card">
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>🛟</div>
            <h3 className="h2">Reliable Support</h3>
            <p className="muted">
              Raise support tickets with screenshots, track progress, and get transparent resolution updates.
            </p>
          </div>
        </div>
      </section>

      {!isAuthenticated && (
        <section style={{ marginTop: '48px' }}>
          <div className="card shadow" style={{ textAlign: 'center', padding: '32px' }}>
            <h2 className="h1">Ready to Get Started?</h2>
            <p className="muted" style={{ margin: '16px 0 24px' }}>
              Join thousands of candidates and recruiters who trust talixjobs for their career journey.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" className="btn">
                Sign Up as Candidate
              </Link>
              <Link to="/register" className="btn secondary">
                Post Jobs as Recruiter
              </Link>
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default Home;
